import html2canvas from "html2canvas";
import { Download, Printer, Share2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";

const QrContainer = ({ openEditModal, setOpenEditModal, qr, isLoading }) => {
  const printRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [imageSrc, setImageSrc] = useState("");

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `@media print {
      @page {
        size: 393px 810px;
        margin: 10px;
      }
    }`,
  });

  const handleSaveAsPng = async () => {
    if (printRef.current === null) {
      return;
    }
    setIsDownloading(true);

    // Add useCORS to handle cross-origin images
    html2canvas(printRef.current, { useCORS: true }).then((canvas) => {
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = "qr.png";
      link.click();

      setIsDownloading(false);
    });
  };

  const handleShare = () => {
    if (printRef.current === null) {
      return;
    }
    setIsSharing(true);

    // Add useCORS to handle cross-origin images
    html2canvas(printRef.current, { useCORS: true }).then((canvas) => {
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], "qr.png", { type: "image/png" });

          // Check if the file can be shared
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            navigator
              .share({
                files: [file],
                title: "MagicReview QR",
                text: "Scan the QR to generate reviews!",
              })
              .then(() => {
                toast.success("Image shared successfully");
                setIsSharing(false);
              })
              .catch((error) => {
                toast.error(`Error sharing image: ${error}`);
                setIsSharing(false);
              });
          } else {
            toast.error(
              "File sharing not supported for this type or the browser does not support sharing."
            );
            setIsSharing(false);
          }
        } else {
          toast.error("Image generation failed");
          setIsSharing(false);
        }
      });
    });
  };

  useEffect(() => {
    // Create a URL for the Blob
    if (qr) {
      const url = URL.createObjectURL(qr);

      // Set the URL as the image source
      setImageSrc(url);

      // Clean up the URL object when the component unmounts
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [qr]);

  return (
    <Dialog
      open={openEditModal}
      onOpenChange={() => {
        setOpenEditModal(!openEditModal);
      }}
    >
      <DialogContent className="bg-white !rounded-2xl !max-w-fit p-5 lg:p-10 font-poppins">
        <DialogHeader className={"hidden"}>
          <DialogTitle></DialogTitle>
        </DialogHeader>

        {/* what user sees */}
        <div className="flex flex-col text-center pt-10" ref={printRef}>
          <p className="text-center font-poppins font-medium text-xl text-black  mb-7">
            Scan QR Code
          </p>

          {isLoading ? (
            <div className="flex items-center justify-center w-[333px] h-[333px] mx-10">
              <div className="w-5 h-5 border-t-2 border-b-2 border-secondary rounded-full animate-spin"></div>
            </div>
          ) : (
            <img
              src={imageSrc}
              alt=""
              className="w-[55%] border border-[#BFBFBF] mx-auto rounded-[12px] mb-10"
            />
          )}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 items-center gap-3">
          <Button onClick={() => handlePrint()} className="gap-3">
            <Printer className="size-5 flex-shrink-0" /> Print
          </Button>
          <Button className="gap-3" onClick={handleShare}>
            {isSharing ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-t-2 border-b-2 border-white group-hover:border-secondary rounded-full animate-spin"></div>
              </div>
            ) : (
              <>
                <Share2 className="size-5 flex-shrink-0" /> Share
              </>
            )}
          </Button>
          <Button className="gap-3" onClick={handleSaveAsPng}>
            {isDownloading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-t-2 border-b-2 border-white group-hover:border-secondary rounded-full animate-spin"></div>
              </div>
            ) : (
              <>
                <Download className="size-5 flex-shrink-0" /> Save
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QrContainer;
